import { TProcedure, TTemplate } from "../utils/types";

export const capitalizeString = (
  inputString: string | null | undefined,
): string => {
  if (!inputString || typeof inputString !== "string") return "";

  if (inputString === "BBL") return inputString;

  let words;
  let capitalizedString;
  if (inputString.includes("_")) {
    words = inputString.split("_");
    capitalizedString = words
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(" ");
  } else {
    words = inputString.split(" ");
    capitalizedString = words
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(" ");
  }

  return capitalizedString;
};

export const convertDateToYYYYMMDD = (date: string): string => {
  const tempDate = new Date(date);
  const year = tempDate.getFullYear();
  const month = String(tempDate.getMonth() + 1).padStart(2, "0");
  const day = String(tempDate.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

export function isFaceProcedure(procedure: TProcedure): boolean {
  const faceProcedures: TProcedure[] = [
    "LIP_FILLER",
    "FACE_LIFT",
    "RHINOPLASTY",
    "CHIN_LIPOSUCTION",
    "HAIR_TRANSPLANT",
    "BLEPHAROPLASTY",
    "RHYTIDECTOMY",
    "OTOPLASTY",
    "FOREHEAD_LIFT",
    "CHIN_SURGERY",
    "CHEEK_AUGMENTATION",
    "BOTOX_INJECTIONS",
    "DERMAL_FILLERS",
    "LASER_TREATMENTS",
    "MICRODERMABRASION",
    "LASER_HAIR_REMOVAL",
  ];

  return faceProcedures.includes(procedure);
}

export function isTorsoProcedure(procedure: TProcedure): boolean {
  const torsoProcedures: TProcedure[] = [
    "BREAST_AUGMENTATION",
    "BREAST_REDUCTION",
    "BREAST_LIFT",
    "BREAST_LIFT_AND_AUGMENTATION",
  ];

  return torsoProcedures.includes(procedure);
}

export function isBodyProcedure(procedure: TProcedure): boolean {
  const bodyProcedures: TProcedure[] = [
    "ABDOMEN_LIPOSUCTION",
    "TUMMY_TUCK",
    "BBL",
  ];

  return bodyProcedures.includes(procedure);
}

export function createUUID(): string {
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, function (c) {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

export const convertATemplateWithHeightToAnotherHeight = (
  template: TTemplate,
  randomHeight: number = 1000,
): TTemplate => {
  const templateCopy = JSON.parse(JSON.stringify(template));
  const templateHeight = templateCopy.outerContainer.height;
  const ratio = randomHeight / templateHeight;
  templateCopy.outerContainer.width = templateCopy.outerContainer.height;

  const scaleValues = (obj: any): any => {
    if (typeof obj === "number") {
      return obj * ratio;
    } else if (Array.isArray(obj)) {
      return obj.map(scaleValues);
    } else if (typeof obj === "object" && obj !== null) {
      return Object.fromEntries(
        Object.entries(obj).map(([key, value]) => {
          if (key === "zIndex" || key === "assetId") {
            return [key, value]; // Skip scaling for zIndex and asset references
          }
          return [key, scaleValues(value)];
        }),
      );
    }
    return obj;
  };

  return scaleValues(templateCopy);
};

export const getS3KeyFromPresignedUrlForGallery = (presignedUrl: string) => {
  /**
   * Extracts the S3 key from the presigned URL
   * @param {string} presignedUrl - The presigned URL to extract the key from.
   * @returns {string} The extracted S3 key.
   */
  try {
    // Parse the URL to get its components
    const parsedUrl = new URL(presignedUrl);

    // The path part of the URL contains the S3 key
    const s3Key = parsedUrl.pathname
      .substring(1)
      .replace("_thumbnail.jpg", ".png");

    return s3Key;
  } catch (error) {
    console.error("Error parsing the URL:", error);
    throw error;
  }
};

export const isValidateTemplate = (template: any): template is TTemplate => {
  // Helper validation functions
  const isValidImageObject = (image: any): boolean => {
    return (
      image &&
      typeof image === "object" &&
      typeof image.src === "string" &&
      isValidPositionObject(image.position) &&
      isValidSizeObject(image.size) &&
      typeof image.containerStyle === "object" &&
      typeof image.imageStyle === "object"
    );
  };

  const isValidTextObject = (text: any): boolean => {
    return (
      text &&
      typeof text === "object" &&
      typeof text.content === "string" &&
      isValidPositionObject(text.position) &&
      typeof text.containerStyle === "object" &&
      typeof text.textStyle === "object" &&
      typeof text.textStyle.fontSize === "number"
    );
  };

  const isValidPositionObject = (position: any): boolean => {
    return (
      position &&
      typeof position === "object" &&
      typeof position.x === "number" &&
      typeof position.y === "number"
    );
  };

  const isValidSizeObject = (size: any): boolean => {
    return (
      size &&
      typeof size === "object" &&
      typeof size.height === "string" &&
      typeof size.width === "string"
    );
  };

  const isValidImageContainerObject = (container: any): boolean => {
    return (
      container &&
      typeof container === "object" &&
      isValidPositionObject(container.position) &&
      isValidSizeObject(container.size) &&
      typeof container.containerStyle === "object" &&
      typeof container.imageStyle === "object"
    );
  };

  if (!template || typeof template !== "object") return false;

  // Validate outerContainer
  if (!template.outerContainer || typeof template.outerContainer !== "object")
    return false;

  const outerContainerHeightString = `${template.outerContainer.height}`;
  if (outerContainerHeightString.includes("px")) return false;

  // Validate images array
  if (!Array.isArray(template.images)) return false;
  for (const image of template.images) {
    if (!isValidImageObject(image)) return false;
  }

  // Validate texts array
  if (!Array.isArray(template.texts)) return false;
  for (const text of template.texts) {
    if (!isValidTextObject(text)) return false;
  }

  // Validate divs array
  if (!Array.isArray(template.divs)) return false;
  for (const div of template.divs) {
    if (!div || typeof div !== "object") return false;
  }

  // Validate beforeImage and afterImage
  if (
    !isValidImageContainerObject(template.beforeImage) ||
    !isValidImageContainerObject(template.afterImage)
  )
    return false;

  return true;
};

/**
 * Updates the template by replacing placeholders with clinic logos.
 * - If `clinicLogoSquare` is provided, it replaces all instances of "TEMPLATE_LOGO_SQUARE".
 * - If `clinicLogoRectangle` is provided, it replaces all instances of "TEMPLATE_LOGO_RECTANGLE".
 * - If either parameter is `null`, the corresponding placeholder remains unchanged.
 *
 * @param template - The template object containing images, text, and styles.
 * @param clinicLogoSquare - The URL of the square clinic logo, or `null` to keep "TEMPLATE_LOGO_SQUARE" unchanged.
 * @param clinicLogoRectangle - The URL of the rectangular clinic logo, or `null` to keep "TEMPLATE_LOGO_RECTANGLE" unchanged.
 * @returns A new template object with the updated images.
 */
export const updateTemplateWithClinicLogo = (
  template: TTemplate,
  clinicLogoSquare: string | null,
  clinicLogoRectangle: string | null,
): TTemplate => {
  return {
    ...template,
    images: template.images.map((image) => {
      let updatedSrc = image.src;

      if (clinicLogoSquare && updatedSrc.includes("TEMPLATE_LOGO_SQUARE")) {
        updatedSrc = clinicLogoSquare;
      } else if (
        !clinicLogoSquare &&
        updatedSrc.includes("TEMPLATE_LOGO_SQUARE")
      ) {
        return image; // Keep the existing placeholder
      }

      if (
        clinicLogoRectangle &&
        updatedSrc.includes("TEMPLATE_LOGO_RECTANGLE")
      ) {
        updatedSrc = clinicLogoRectangle;
      } else if (
        !clinicLogoRectangle &&
        updatedSrc.includes("TEMPLATE_LOGO_RECTANGLE")
      ) {
        return image; // Keep the existing placeholder
      }

      return updatedSrc !== image.src ? { ...image, src: updatedSrc } : image;
    }),
  };
};

/**
 * Validates an image URL by making a GET request.
 * If the request succeeds, returns the provided URL; otherwise, returns null.
 *
 * @param clinicLogo - The image URL to validate.
 * @returns A promise that resolves to the valid URL or null if the fetch fails.
 */
export const validateClinicLogo = async (
  clinicLogo: string,
): Promise<string | null> => {
  try {
    const response = await fetch(clinicLogo, { method: "GET" });
    if (!response.ok) {
      throw new Error("Invalid image URL");
    }
    return clinicLogo;
  } catch (error) {
    console.warn("Failed to fetch clinic logo:", error);
    return null;
  }
};

// Check if we're in the local environment
export const isLocalEnv =
  process.env.EXPO_PUBLIC_ENV !== "dev" &&
  process.env.EXPO_PUBLIC_ENV !== "staging" &&
  process.env.EXPO_PUBLIC_ENV !== "production";

export const pollData = async (
  url: string,
  signal: AbortSignal,
  interval: number = 5000,
  maxRetries: number = 1000,
): Promise<any> => {
  let retries = 0;

  while (retries < maxRetries) {
    const pollResponse = await fetch(url, { method: "GET", signal });
    if (pollResponse.ok) {
      return pollResponse;
    }

    retries += 1;
    await new Promise((resolve) => setTimeout(resolve, interval));
  }

  throw new Error("Polling exceeded max retries");
};
